import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  Alert,
  Autocomplete,
} from '@mui/material';
import { UserDto } from '@cloudcost/shared-types/users';
import { T } from '@/theme/tokens';

// Shared input styling — #E1E7F0 borders, radius 9, per CloudCost.dc design
const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: `${T.radiusSm}px`,
    fontSize: '0.9rem',
    '& fieldset': { borderColor: T.borderInput },
    '&:hover fieldset': { borderColor: T.borderInput },
    '&.Mui-focused fieldset': { borderColor: T.primary },
  },
  '& .MuiInputLabel-root': { color: T.textFaint },
  '& .MuiInputLabel-root.Mui-focused': { color: T.primary },
} as const;

interface DisputeDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (title: string, receiverId: number) => void;
  defaultReceiverId: number | null;
  currentUserId: number;
  showUserSelector: boolean;
  availableUsers?: UserDto[];
  disputeTitle?: string;
  zIndex?: number;
}

/**
 * Reusable dispute dialog component for creating disputes
 * Supports both simple disputes (with default receiver) and disputes with user selection
 */
export const DisputeDialog: React.FC<DisputeDialogProps> = ({
  open,
  onClose,
  onSubmit,
  defaultReceiverId,
  currentUserId,
  showUserSelector,
  availableUsers = [],
  disputeTitle: initialTitle = '',
  zIndex,
}) => {
  const [disputeTitle, setDisputeTitle] = useState(initialTitle);
  const [selectedReceiverId, setSelectedReceiverId] = useState<number | null>(
    defaultReceiverId,
  );
  const [alert, setAlert] = useState(false);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setDisputeTitle(initialTitle);
      setSelectedReceiverId(defaultReceiverId);
      setAlert(false);
    } else {
      setDisputeTitle('');
      setSelectedReceiverId(defaultReceiverId);
      setAlert(false);
    }
  }, [open, initialTitle, defaultReceiverId]);

  const handleClose = () => {
    setDisputeTitle('');
    setSelectedReceiverId(defaultReceiverId);
    setAlert(false);
    onClose();
  };

  const handleSubmit = () => {
    const trimmedTitle = disputeTitle.trim();
    if (trimmedTitle.length < 1) {
      setAlert(true);
      return;
    }

    const receiverId = selectedReceiverId || defaultReceiverId;
    if (!receiverId) {
      setAlert(true);
      return;
    }

    if (Number(currentUserId) === Number(receiverId)) {
      setAlert(true);
      return;
    }

    onSubmit(trimmedTitle, receiverId);
    handleClose();
  };

  const receiverId = selectedReceiverId || defaultReceiverId;
  const isSelfDispute = Number(currentUserId) === Number(receiverId);
  
  // Determine the specific error message
  const getErrorMessage = () => {
    if (isSelfDispute) {
      return "You can't open a dispute to yourself";
    }
    const trimmedTitle = disputeTitle.trim();
    if (trimmedTitle.length < 1) {
      return "Dispute title can't be empty!";
    }
    if (!receiverId) {
      if (showUserSelector) {
        return "Please select a user for the dispute";
      }
      return "Please select a user for the dispute";
    }
    return "Dispute title can't be empty!";
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          borderRadius: `${T.radius}px`,
          border: `1px solid ${T.border}`,
          boxShadow: T.popShadow,
          minWidth: 420,
          fontFamily: T.font,
        },
      }}
      sx={
        zIndex
          ? {
              zIndex: `${zIndex} !important`,
              '& .MuiDialog-container': {
                zIndex: `${zIndex} !important`,
              },
              '& .MuiBackdrop-root': {
                zIndex: `${zIndex - 1} !important`,
              },
              '& .MuiDialog-paper': {
                zIndex: `${zIndex + 1} !important`,
                position: 'relative',
              },
            }
          : undefined
      }>
      <DialogTitle
        sx={{
          fontWeight: 900,
          fontSize: '1.2rem',
          color: T.text,
          letterSpacing: '-.2px',
          pb: 1,
        }}>
        Open Dispute
      </DialogTitle>
      <div>
        <DialogContent>
          {isSelfDispute ? (
            <Alert severity="error">You can't open a dispute to yourself</Alert>
          ) : (
            <>
              <DialogContentText sx={{ color: T.textFaint, fontSize: '0.85rem', mb: 1.5 }}>
                Please add title to the dispute.
              </DialogContentText>
              <TextField
                autoFocus
                margin="dense"
                id="dispute-title"
                label="Dispute Title"
                fullWidth
                variant="outlined"
                size="small"
                sx={inputSx}
                value={disputeTitle}
                onChange={(e) => {
                  setDisputeTitle(e.target.value);
                  setAlert(false);
                }}
              />
              {showUserSelector && (
                <Autocomplete
                  sx={{
                    mt: 2,
                    '& .MuiAutocomplete-popper': {
                      zIndex: zIndex ? `${zIndex + 1} !important` : undefined,
                    },
                    '& .MuiAutocomplete-listbox': {
                      zIndex: zIndex ? `${zIndex + 1} !important` : undefined,
                    },
                  }}
                  slotProps={{
                    popper: {
                      sx: {
                        zIndex: zIndex ? `${zIndex + 1} !important` : undefined,
                      },
                      style: {
                        zIndex: zIndex ? zIndex + 1 : undefined,
                      },
                    },
                  }}
                  options={availableUsers || []}
                  getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.username})`}
                  value={
                    availableUsers?.find((u) => u.id === selectedReceiverId) || null
                  }
                  onChange={(event, newValue) => {
                    setSelectedReceiverId(newValue?.id || null);
                    setAlert(false);
                  }}
                  loading={availableUsers.length === 0}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select User"
                      variant="outlined"
                      size="small"
                      margin="dense"
                      sx={inputSx}
                      helperText={availableUsers.length === 0 ? "Loading users..." : undefined}
                    />
                  )}
                />
              )}
            </>
          )}
        </DialogContent>
        {alert === true && (
          <Alert severity="error">
            {getErrorMessage()}
          </Alert>
        )}
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button
            onClick={handleClose}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              color: T.textFaint,
              borderRadius: `${T.radiusSm}px`,
            }}>
            Cancel
          </Button>
          {!isSelfDispute && (
            <Button
              onClick={handleSubmit}
              variant="contained"
              disableElevation
              sx={{
                textTransform: 'none',
                fontWeight: 800,
                borderRadius: `${T.radiusSm}px`,
                px: 2.25,
                backgroundColor: T.primary,
                '&:hover': { backgroundColor: T.primaryHover },
              }}>
              Open Dispute
            </Button>
          )}
        </DialogActions>
      </div>
    </Dialog>
  );
};



